<template>
  <div v-show="total" class="w-full">
    <ul
      class="flex justify-center align-middle text-cdp-black-500"
      :class="classes.wrapper"
    >
      <li :class="[element(false), prevElementIsDisabled]">
        <button :class="[button(false), prevButtonIsDisabled]" @click="previousPage">
          <icon name="ic_common_arrow01_left" class="h-4 w-4" />
        </button>
      </li>
      <template v-for="(page, idx) in pages" :key="`pages-${idx}`">
        <li :class="[element(page == currentPage)]">
          <button :class="[button(page == currentPage)]" @click="setCurrent(page)">
            {{ page }}
          </button>
        </li>
      </template>
      <li :class="[element(false), nextElementIsDisabled]">
        <button :class="[button(false), nextButtonIsDisabled]" @click="nextPage()">
          <icon name="ic_common_arrow01_right" class="h-4 w-4" />
        </button>
      </li>
    </ul>
  </div>
</template>
<script>
import { defineComponent, ref, computed, watch } from 'vue';

export default defineComponent({
  name: 'ComponentsPaginAtions',
  props: {
    modelValue: {
      type: Number,
      default: 1
    },
    total: {
      type: Number,
      default: null
    },
    perPage: {
      type: Number,
      default: 10
    },
    limit: {
      type: Number,
      default: 5
    }
  },
  emits: ['update:modelValue', 'change'],
  setup(props, { emit }) {
    const classes = {
      wrapper: 'text-center mx-auto gap-2',
      element: 'w-8 h-8 flex',
      activeElement: 'w-8 h-8',
      disabledElement: 'w-8 h-8 cursor-not-allowed',
      activeButton:
        'w-8 h-8 bg-cdp-main-primary-dark w-full h-full text-cdp-white rounded-md duration-300',
      disabledButton: 'opacity-25 w-full h-full pointer-events-none duration-300',
      button:
        'w-8 h-8 w-full h-full rounded-md duration-300 flex justify-center items-center'
    };

    const currentPage = ref(props.modelValue);
    const totalPagination = computed(() => {
      if (props.perPage <= 0) {
        return 0;
      }

      return Math.ceil(props.total / props.perPage);
    });
    const pages = computed(() => {
      const numShown = Math.min(props.limit, totalPagination.value);
      let first = currentPage.value - Math.floor(numShown / 2);
      first = Math.max(first, 1);
      first = Math.min(first, totalPagination.value - numShown + 1);
      return [...Array(numShown)].map((k, i) => i + first);
    });

    const prevElementIsDisabled = computed(() => {
      return currentPage.value === null || currentPage.value <= 1
        ? classes.disabledElement
        : '';
    });
    const nextElementIsDisabled = computed(() => {
      return currentPage.value === null || currentPage.value >= totalPagination.value
        ? classes.disabledElement
        : '';
    });
    const prevButtonIsDisabled = computed(() => {
      return currentPage.value === null || currentPage.value <= 1
        ? classes.disabledButton
        : '';
    });
    const nextButtonIsDisabled = computed(() => {
      return currentPage.value === null || currentPage.value >= totalPagination.value
        ? classes.disabledButton
        : '';
    });

    // this is thousands separators
    const totalCount = computed(() => {
      return props.total.toLocaleString();
    });

    watch(
      () => props.modelValue,
      (page) => {
        currentPage.value = page;
      }
    );

    function setCurrent(page) {
      currentPage.value = page;
      emit('update:modelValue', page);
      emit('change');
    }
    function previousPage() {
      currentPage.value === null
        ? this.setCurrent(1)
        : this.setCurrent(Math.max(currentPage.value - 1, 1));
    }
    function nextPage() {
      currentPage.value === null
        ? this.setCurrent(totalPagination.value)
        : setCurrent(Math.min(currentPage.value + 1, totalPagination.value));
    }
    function firstPage() {
      setCurrent(1);
    }
    function lastPage() {
      setCurrent(totalPagination.value);
    }

    function element(condition) {
      return condition ? classes.activeElement : classes.element;
    }
    function button(condition) {
      return condition ? classes.activeButton : classes.button;
    }

    return {
      classes,
      currentPage,
      totalPagination,
      pages,
      prevElementIsDisabled,
      nextElementIsDisabled,
      prevButtonIsDisabled,
      nextButtonIsDisabled,
      totalCount,
      setCurrent,
      previousPage,
      nextPage,
      firstPage,
      lastPage,
      element,
      button
    };
  }
});
</script>